























































































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import marked from 'marked';

import { Poi, PoiState, PoiLink } from '@/models/poi';
import { Sponsor } from '@/models/sponsor';
import { Question, Answer } from '@/models/quiz';

import PoiMediaGallery from '@/components/poi-gallery.vue';
import SlideToUnlock from '@/components/slide-to-unlock.vue';
import PoiTextAudio from '@/components/poi-text-audio.vue';
import SponsorCard from '@/components/sponsor-card.vue';
import FavoriteHelper from '@/services/favorite-helper.ts';
import QuizRunner from '@/services/quiz-runner.ts';

import haversine from 'haversine';

@Component({
    components: {
        PoiMediaGallery,
        SlideToUnlock,
        PoiTextAudio,
        SponsorCard
    }
})
export default class PoiDetailPage extends Vue {
    @Prop(String) public poiId: string;

    public poi: Poi = {} as Poi;
    public isFavorite: boolean = false;
    public quizQuestion: Question = null;
    public quizAnswered: boolean = false;

    private initialIsFavorite: boolean = false;
    private textAudioPlayer: PoiTextAudio;

    public async created() {
        await this.$store.waitUntilReady();
        this.poi = this.$store.getPoi(parseInt(this.poiId, 10));
        this.isFavorite = FavoriteHelper.isFavorite(this.$store, this.poi.id);
        this.initialIsFavorite = FavoriteHelper.isFavorite(this.$store, this.poi.id);
        this.updateQuizState();
    }

    public mounted() {
        this.textAudioPlayer = this.$refs.textAudioPlayer as PoiTextAudio;
    }

    public onDisappearing(e, p) {
        if (this.textAudioPlayer) {
            this.textAudioPlayer.stopAudio();
        }

        // store favorites
        if (this.initialIsFavorite !== FavoriteHelper.isFavorite(this.$store, this.poi.id)) {
            this.$store.saveFavoritePoiIds();
        }
    }

    public get filteredLinks(): PoiLink[] {
        const filteredLinks: PoiLink[] = [];

        if (this.poi && this.poi.links) {
            for (const link of this.poi.links) {
                if (link.text && link.url) {
                    filteredLinks.push(link);
                }
            }
        }

        return filteredLinks;
    }

    public get isLocked(): boolean {
        return this.poi.state === PoiState.LOCKED;
    }

    public get isUnlocked(): boolean {
        return this.poi.state === PoiState.UNLOCKED;
    }

    public get isReadyToUnlock(): boolean {
        return this.poi.state === PoiState.READY_TO_UNLOCK;
    }

    public get hasTopics(): boolean {
        return this.poi.topics !== undefined && this.poi.topics.length > 0;
    }

    public get hasSponsors(): boolean {
        return this.poi.sponsors !== undefined && this.poi.sponsors.length > 0;
    }

    public get allowTestUnlock() {
        return process.env.VUE_APP_DEBUG === 'enabled';
    }

    public get quizEnabled() {
        return process.env.VUE_APP_QUIZ === 'enabled';
    }

    public startQuiz() {
        if (!this.quizQuestion) return;

        const runner = new QuizRunner(this.quizQuestion, this.updateQuizState);
        runner.start();
    }

    public updateQuizState() {
        if (!this.$store.isReady || !this.$store.quiz) return false;
        const question = this.$store.quiz.questions.find(q => q.poi === this.poi.id);
        this.quizQuestion = question;
        this.quizAnswered = question && this.$store.quizAnswers.find(a => a.questionId === question.id) !== undefined;
    }

    public onUnlocked() {
        this.$store.poiJournalService.setPoiUnlocked(this.poi);

        this.$ga.event({
            eventCategory: 'poi',
            eventAction: 'unlocked',
            eventLabel: this.poi.number
        });
    }

    public playAudioForTextId(audioUrl) {
        this.textAudioPlayer.playAudio(audioUrl);

        this.$ga.event({
            eventCategory: 'poi',
            eventAction: 'audioTextPlay',
            eventLabel: this.poi.number
        });
    }

    public markdown(value: any) {
        if (!value) return '';
        return marked(value, { pedantic: true });
    }

    public resolveTopic(topicId: number): string {
        return this.$t('poi_topics.' + topicId) as string;
    }

    public resolveSponsor(sponsorId: number): Sponsor {
        return this.$store.sponsors.find(s => s.id === sponsorId);
    }

    public toggleFavorite() {
        if (this.isFavorite) {
            FavoriteHelper.removeFromFavorites(this.$store, this.poi.id);
            this.$ga.event({
                eventCategory: 'poi',
                eventAction: 'favoriteRemoved',
                eventLabel: this.poi.number
            });
        } else {
            FavoriteHelper.addToFavorites(this.$store, this.poi.id);
            this.$ga.event({
                eventCategory: 'poi',
                eventAction: 'favoriteAdded',
                eventLabel: this.poi.number
            });
        }

        this.isFavorite = FavoriteHelper.isFavorite(this.$store, this.poi.id);

        // request a map update if coming from favorites and this changed
        if (this.$store.mapShowFavorites) {
            this.$store.mapChangeSourceRequest = true;
        }
    }

    public debugUnlockPoi() {
        this.$store.poiJournalService.setPoiReadyToUnlock(this.poi);
    }

    public requestHighAccuracyLocation() {
        this.$store.geolocator.requestHighAccuracyLocation(
            (pos) => {
                // custom callback -> for success if the could be determine and the distance is greater than 75 meter
                // then the Error Sheet will appear
                const distance = haversine(this.poi.coordinates, pos.coords, { unit: 'meter' });
                const unlockDistance = 75;
                if (distance > unlockDistance) {
                    this.showUnlockFailedAlertMessage();
                }
            },
            (err) => {
                // custom error callback if position could not be determine show the Sheet anyway!
                this.showUnlockFailedAlertMessage();
            }
        );
    }

    public showUnlockFailedAlertMessage() {
        const title = this.$t('poi.geolocate_title') as string;
        const text = this.$t('poi.geolocate_text') as string;
        this.$f7.dialog.alert(text, title);
    }
}
